import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { toast } from "sonner";
import { Mail, Trash2, Check, Phone, Car, Loader2, Inbox } from "lucide-react";

export default function AdminMessages() {
  const [msgs, setMsgs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/contact-messages");
      setMsgs(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    try {
      await api.patch(`/contact-messages/${id}/read`);
      setMsgs((arr) => arr.map((m) => (m.id === id ? { ...m, read: true } : m)));
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questo messaggio?")) return;
    try {
      await api.delete(`/contact-messages/${id}`);
      setMsgs((arr) => arr.filter((m) => m.id !== id));
      toast.success("Messaggio eliminato");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const unread = msgs.filter((m) => !m.read).length;

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#1e293b]">Messaggi ricevuti</h1>
          <p className="text-sm text-slate-500 mt-1">
            {msgs.length} total{msgs.length === 1 ? "e" : "i"}
            {unread > 0 && (
              <span className="ml-3 px-2.5 py-0.5 rounded-full bg-[#76BA1B] text-white text-[10px] font-bold uppercase tracking-wider">
                {unread} da leggere
              </span>
            )}
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-slate-400">
          <Loader2 className="animate-spin mr-3" /> Caricamento…
        </div>
      ) : msgs.length === 0 ? (
        <div className="bg-white rounded-2xl p-16 text-center border border-dashed border-slate-200">
          <Inbox className="mx-auto text-slate-300 mb-4" size={32} />
          <div className="text-slate-500">Nessun messaggio per ora. Le richieste dal sito arriveranno qui.</div>
        </div>
      ) : (
        <div data-testid="admin-messages-list" className="space-y-4">
          {msgs.map((m) => (
            <div
              key={m.id}
              data-testid={`admin-msg-${m.id}`}
              className={`bg-white rounded-2xl p-5 border transition ${
                m.read
                  ? "border-slate-200"
                  : "border-[#76BA1B]/60 shadow-[0_8px_24px_rgba(118,186,27,0.10)]"
              }`}
            >
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-heading text-lg font-bold text-[#1e293b]">{m.name}</span>
                    {!m.read && (
                      <span className="px-2 py-0.5 rounded-full bg-[#76BA1B] text-white text-[9px] font-bold uppercase tracking-wider">
                        Nuovo
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-slate-500">
                    <a href={`mailto:${m.email}`} className="flex items-center gap-1 hover:text-[#76BA1B]">
                      <Mail size={12} /> {m.email}
                    </a>
                    {m.phone && (
                      <a href={`tel:${m.phone}`} className="flex items-center gap-1 hover:text-[#76BA1B]">
                        <Phone size={12} /> {m.phone}
                      </a>
                    )}
                    {m.vehicle_id && (
                      <span className="flex items-center gap-1 text-slate-400">
                        <Car size={12} /> {m.vehicle_id.slice(0, 8)}…
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-[11px] text-slate-400 whitespace-nowrap">
                  {new Date(m.created_at).toLocaleString("it-IT")}
                </div>
              </div>
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line bg-slate-50 rounded-xl p-4 border border-slate-100">
                {m.message}
              </p>
              <div className="mt-4 flex gap-2">
                {!m.read && (
                  <button
                    data-testid={`mark-read-${m.id}`}
                    onClick={() => markRead(m.id)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#f0fdf4] text-[#5fa015] text-xs font-semibold hover:bg-[#dcfce7] transition"
                  >
                    <Check size={12} /> Segna come letto
                  </button>
                )}
                <button
                  data-testid={`delete-msg-${m.id}`}
                  onClick={() => del(m.id)}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-red-50 text-red-500 text-xs hover:bg-red-100 transition"
                >
                  <Trash2 size={12} /> Elimina
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}
